/**
 * Admin feedback triage list + maintenance panel (ADR-0074 §3.4 / §3.5).
 *
 * Hub-admin only. Server-side gating is `requireHubRole(['hub_admin'])`;
 * a tenant user hitting /api/admin/feedback gets 403.
 */

import { useEffect, useMemo, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { formatTimestamp } from "../lib/formatTime.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { PageLayout } from "../components/PageLayout.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import type {
  FeedbackListRow,
  FeedbackSeverity,
  FeedbackStatus,
} from "../lib/feedback.js";
import { AdminFeedbackDetail } from "./AdminFeedbackDetail.js";

type SeverityFilter = "all" | FeedbackSeverity;
type StatusFilter = "all" | FeedbackStatus;

const ALL_SEVERITIES: FeedbackSeverity[] = ["cosmetic", "minor", "major", "critical"];
const ALL_STATUSES: FeedbackStatus[] = ["open", "closed"];

function severityPillClass(s: string): string {
  if (s === "critical") return "pill pillBad";
  if (s === "major") return "pill pillWarn";
  return "pill";
}

function statusPillClass(s: string): string {
  return s === "open" ? "pill pillWarn" : "pill";
}

function severityValuesFor(filter: SeverityFilter): FeedbackSeverity[] | null {
  if (filter === "all") return null;
  return [filter];
}

function statusValuesFor(filter: StatusFilter): FeedbackStatus[] | null {
  if (filter === "all") return null;
  return [filter];
}

interface ListResponse {
  items: FeedbackListRow[];
  next_cursor: string | null;
}

interface PreviewResponse { count: number }
interface BulkDeleteResponse { deleted: number }

const PREVIEW_DEBOUNCE_MS = 300;
const BULK_MAX = 10000;

const HOVER_TIGHTEN = "Tighten the filters — bulk delete is capped at 10,000 rows per call. Run again to delete more.";

export function AdminFeedback() {
  const [items, setItems] = useState<FeedbackListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [maintOpen, setMaintOpen] = useState(false);

  // Debounce search input.
  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(search), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const loadList = useMemo(() => {
    return async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        const sev = severityValuesFor(severityFilter);
        if (sev) params.set("severity", sev.join(","));
        const stat = statusValuesFor(statusFilter);
        if (stat) params.set("status", stat.join(","));
        else params.set("status", ALL_STATUSES.join(","));
        if (searchDebounced.trim().length > 0) params.set("q", searchDebounced.trim());
        params.set("limit", "100");
        const res = await apiFetch<ListResponse>(`/api/admin/feedback?${params.toString()}`);
        setItems(res.items);
        setNextCursor(res.next_cursor ?? null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    };
  }, [severityFilter, statusFilter, searchDebounced]);

  useEffect(() => { void loadList(); }, [loadList]);

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      const sev = severityValuesFor(severityFilter);
      if (sev) params.set("severity", sev.join(","));
      const stat = statusValuesFor(statusFilter);
      if (stat) params.set("status", stat.join(","));
      else params.set("status", ALL_STATUSES.join(","));
      if (searchDebounced.trim().length > 0) params.set("q", searchDebounced.trim());
      params.set("limit", "100");
      params.set("cursor", nextCursor);
      const res = await apiFetch<ListResponse>(`/api/admin/feedback?${params.toString()}`);
      setItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.next_cursor ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <PageLayout title="Feedback">
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="toolbarGroup">
          <label className="muted" htmlFor="sev-filter">Severity</label>
          <select id="sev-filter" className="select" value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}>
            <option value="all">All</option>
            {ALL_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="muted" htmlFor="stat-filter">Status</label>
          <select id="stat-filter" className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="all">All</option>
            <option value="open">open</option>
            <option value="closed">closed</option>
          </select>
          <input
            className="input"
            type="text"
            placeholder="Search messages…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 220 }}
          />
        </div>
        <div className="toolbarGroup" style={{ marginLeft: "auto" }}>
          <button className="btn" onClick={() => setMaintOpen((v) => !v)}>{maintOpen ? "Close maintenance" : "Maintenance"}</button>
        </div>
      </div>

      {maintOpen ? <MaintenancePanel onChanged={() => void loadList()} /> : null}

      {error ? <ErrorState detail={error} /> : null}
      {loading ? <LoadingState /> : null}
      {!loading && !error && items.length === 0 ? (
        <div className="card"><div className="muted">No feedback yet.</div></div>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <div className="tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Tenant</th>
                <th>Reporter</th>
                <th>Route</th>
                <th>Excerpt</th>
                <th>Submitted</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} style={{ cursor: "pointer" }} onClick={() => setSelectedId(row.id)}>
                  <td><span className={severityPillClass(row.severity)}>{row.severity}</span></td>
                  <td>{row.tenant_display_name ?? "—"}</td>
                  <td>{row.account_display_name ?? row.account_email ?? (row.account_id === null ? "(deleted user)" : "—")}</td>
                  <td>{row.route_label ?? "—"}</td>
                  <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.message_excerpt}
                  </td>
                  <td>{formatTimestamp(row.created_at)}</td>
                  <td><span className={statusPillClass(row.status)}>{row.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {nextCursor ? (
            <div className="actions" style={{ justifyContent: "center", padding: 8 }}>
              <button className="btn" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {selectedId ? (
        <AdminFeedbackDetail
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => void loadList()}
        />
      ) : null}
    </PageLayout>
  );
}

function MaintenancePanel(props: { onChanged: () => void }) {
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [olderThanDays, setOlderThanDays] = useState<30 | 90 | 180 | 365>(180);
  const [count, setCount] = useState<number | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();

  const filterKey = `${severityFilter}|${statusFilter}|${olderThanDays}`;

  // Live preview with 300ms debounce.
  useEffect(() => {
    let cancelled = false;
    const t = window.setTimeout(() => {
      void (async () => {
        setPreviewing(true);
        try {
          const params = new URLSearchParams();
          const sev = severityValuesFor(severityFilter);
          if (sev) params.set("severity", sev.join(","));
          const stat = statusValuesFor(statusFilter);
          if (stat) params.set("status", stat.join(","));
          params.set("older_than_days", String(olderThanDays));
          const res = await apiFetch<PreviewResponse>(`/api/admin/feedback/preview?${params.toString()}`);
          if (!cancelled) setCount(res.count);
        } catch (e) {
          if (!cancelled) setCount(null);
          if (!cancelled) toast.push({ kind: "error", message: e instanceof Error ? e.message : "Preview failed." });
        } finally {
          if (!cancelled) setPreviewing(false);
        }
      })();
    }, PREVIEW_DEBOUNCE_MS);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [filterKey, severityFilter, statusFilter, olderThanDays, toast]);

  async function runDelete() {
    if (count === null || count <= 0 || count > BULK_MAX || busy) return;
    const ok = await confirm.confirm({
      title: "Delete feedback rows",
      detail: `This will permanently delete ${count} feedback row${count === 1 ? "" : "s"} matching the current filters. This cannot be undone.`,
      confirmLabel: `Delete ${count}`,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await apiFetch<BulkDeleteResponse>(
        "/api/admin/feedback/bulk-delete",
        {
          method: "POST",
          body: {
            filters: {
              severity: severityValuesFor(severityFilter),
              status: statusValuesFor(statusFilter),
              older_than_days: olderThanDays,
            },
            expected_count: count,
          },
        }
      );
      toast.push({ kind: "success", message: `Deleted ${res.deleted} feedback rows.` });
      props.onChanged();
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "count_mismatch") {
        toast.push({
          kind: "error",
          message: `Match count drifted; please re-confirm. Now ${(e.data as { current_count?: number }).current_count ?? "?"} rows.`,
        });
      } else if (e instanceof ApiRequestError && e.code === "batch_too_large") {
        toast.push({ kind: "error", message: HOVER_TIGHTEN });
      } else {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : "Delete failed." });
      }
    } finally {
      setBusy(false);
    }
  }

  const buttonDisabled = count === null || count <= 0 || count > BULK_MAX || busy;
  const buttonTitle = count !== null && count > BULK_MAX ? HOVER_TIGHTEN : undefined;

  return (
    <div className="card" style={{ marginBottom: 12, display: "grid", gap: 10 }}>
      <div style={{ fontWeight: 600 }}>Maintenance — bulk delete</div>
      <div className="toolbar">
        <div className="toolbarGroup">
          <label className="muted" htmlFor="m-sev">Severity</label>
          <select id="m-sev" className="select" value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}>
            <option value="all">All</option>
            {ALL_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="muted" htmlFor="m-stat">Status</label>
          <select id="m-stat" className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="all">All</option>
            <option value="open">open</option>
            <option value="closed">closed</option>
          </select>
          <label className="muted" htmlFor="m-age">Older than</label>
          <select id="m-age" className="select" value={olderThanDays} onChange={(e) => setOlderThanDays(Number(e.target.value) as 30 | 90 | 180 | 365)}>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>365 days</option>
          </select>
        </div>
      </div>
      <div className="muted">
        {previewing
          ? "Counting…"
          : count === null
            ? "—"
            : `${count} row${count === 1 ? "" : "s"} match these filters.`}
      </div>
      <div className="actions">
        <button
          className="btn btnDanger"
          disabled={buttonDisabled}
          onClick={() => void runDelete()}
          title={buttonTitle}
        >
          Delete {count ?? 0} rows
        </button>
      </div>
    </div>
  );
}
